import { Control, Controller, FieldError, FieldValues, Path } from 'react-hook-form';
import { MenuItem, TextField, TextFieldProps, Box } from '@mui/material';
import React, { createElement } from 'react';
import ChevronDownIcon from '../../icon/ChevronDownIcon';

export interface OptionValue {
    value: string | number;
    label: string | number | React.ReactElement;
}

export interface SelectElementProps<T extends FieldValues> extends Omit<TextFieldProps, 'name' | 'type' | 'onChange'> {
    name: Path<T>;
    options?: OptionValue[];
    type?: 'string' | 'number';
    parseError?: (error: FieldError) => string;
    onChange?: (value: OptionValue) => void;
    control?: Control<T>;
    multiple?: boolean;
}

const SelectFormField = <T extends FieldValues>({
    name,
    required,
    options = [],
    parseError,
    type,
    control,
    multiple = false,
    ...rest
}: SelectElementProps<T>) => {
    const isNativeSelect = !!rest.SelectProps?.native;
    const ChildComponent = isNativeSelect ? 'option' : MenuItem;

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { onBlur, onChange, value }, fieldState: { invalid, error } }) => {
                if (type === 'number' && typeof value !== 'undefined') {
                    rest.InputLabelProps = rest.InputLabelProps || {};
                    rest.InputLabelProps.shrink = true;
                }
                return (
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <TextField
                        {...rest}
                        name={name}
                        value={value ?? ''}
                        onBlur={onBlur}
                        onChange={(event) => {
                            let item: number | string = event.target.value;
                            if (type === 'number') {
                                item = Number(item);
                            }
                            onChange(item);
                            if (typeof rest.onChange === 'function') {
                                const foundItem = options.find((i) => i.value === item);
                                if (foundItem) {
                                    rest.onChange(foundItem);
                                }
                            }
                        }}
                        select
                        SelectProps={{
                            multiple: multiple,
                            IconComponent: () => (
                                <Box sx={{ mr: '8px', fontSize: '20px', color: (theme) => theme.palette.primary50 }}>
                                    <ChevronDownIcon />
                                </Box>
                            ),
                        }}
                        required={required}
                        error={invalid}
                        helperText={
                            error
                                ? typeof parseError === 'function'
                                    ? parseError(error)
                                    : error.message
                                : rest.helperText
                        }
                    >
                        {isNativeSelect && <option />}
                        {options.map((item, idx) =>
                            createElement(
                                ChildComponent,
                                {
                                    key: `${name}_${idx}`,
                                    value: item.value,
                                },
                                item.label,
                            ),
                        )}
                    </TextField>
                );
            }}
        />
    );
};

export default SelectFormField;
